/**
 * Fix with AI — select a finding, request a proposed fix, track status.
 * Does not auto-apply destructively. Shows a clear unavailable state when orchestrator is missing.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, FindingListItem, RecentFixRunItem, RuntimeResponse } from '../lib/api'
import { LoadingSpinner } from '../components/design-system/Loading'
import { EmptyState } from '../components/design-system/EmptyState'
import { Button } from '../components/design-system/Button'
import { SeverityBadge, StatusBadge } from '../components/design-system/Badge'
import { useLocale } from '../contexts/LocaleContext'

export function FixWithAiPage() {
  const { t } = useLocale()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [runtime, setRuntime] = useState<RuntimeResponse | null>(null)
  const [findings, setFindings] = useState<FindingListItem[]>([])
  const [fixRuns, setFixRuns] = useState<RecentFixRunItem[]>([])
  const [selectedId, setSelectedId] = useState(searchParams.get('finding_id') || '')
  const [targetBranch, setTargetBranch] = useState('fix/reviewer-ai')
  const [baseRef, setBaseRef] = useState('main')
  const [submitting, setSubmitting] = useState(false)
  const [resultMessage, setResultMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        setLoading(true)
        setError(null)
        const [runtimeData, findingsData, fixData] = await Promise.all([
          api.getRuntime(),
          api.listFindings({ status: 'open', page: 1, page_size: 50 }),
          api.listRecentFixRuns(30),
        ])
        if (cancelled) return
        setRuntime(runtimeData)
        setFindings(findingsData.findings)
        setFixRuns(fixData.items)
        if (!selectedId && findingsData.findings[0]) {
          setSelectedId(findingsData.findings[0].id)
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : t('fix.failed'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  async function requestFix() {
    const finding = findings.find((item) => item.id === selectedId)
    if (!finding) {
      setError(t('fix.selectFinding'))
      return
    }
    try {
      setSubmitting(true)
      setError(null)
      setResultMessage(null)
      const created = await api.createFixRun(finding.audit_run_id, {
        finding_ids: [finding.id],
        target_branch: targetBranch,
        base_ref: baseRef,
        auto_commit: false,
        fix_strategy: 'conservative',
      })
      setResultMessage(t('fix.created', { id: created.fix_run_id, status: created.status }))
      const fixData = await api.listRecentFixRuns(30)
      setFixRuns(fixData.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('fix.failed'))
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div data-testid="fix-with-ai-page" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <h1 style={h1Style}>{t('fix.title')}</h1>
        <div style={{ display: 'flex', justifyContent: 'center', minHeight: '240px', alignItems: 'center' }}>
          <LoadingSpinner size="lg" text={t('fix.loading')} />
        </div>
      </div>
    )
  }

  return (
    <div data-testid="fix-with-ai-page" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <header>
        <h1 style={h1Style}>{t('fix.title')}</h1>
        <p style={subStyle}>
          {t('fix.lead')}
        </p>
      </header>

      <div style={bannerStyle(runtime?.orchestrator_configured)}>
        {runtime?.orchestrator_configured
          ? t('fix.orchOk')
          : t('fix.orchOff')}
      </div>

      {error && <div className="card card-error"><p className="text-sm">{error}</p></div>}
      {resultMessage && <div className="card"><p className="text-sm">{resultMessage}</p></div>}

      {findings.length === 0 ? (
          <EmptyState
          title={t('fix.empty')}
          description={t('fix.emptyBody')}
          action={{ label: t('scores.goAudits'), onClick: () => navigate('/audits') }}
        />
      ) : (
        <section style={cardStyle}>
          <h2 style={h2Style}>{t('fix.requestTitle')}</h2>
          <label className="text-sm" style={labelStyle}>
            {t('fix.finding')}
            <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} style={inputStyle}>
              {findings.map((finding) => (
                <option key={finding.id} value={finding.id}>
                  [{finding.severity}] {finding.title}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm" style={labelStyle}>
            {t('fix.branch')}
            <input value={targetBranch} onChange={(e) => setTargetBranch(e.target.value)} style={inputStyle} />
          </label>
          <label className="text-sm" style={labelStyle}>
            {t('fix.baseRef')}
            <input value={baseRef} onChange={(e) => setBaseRef(e.target.value)} style={inputStyle} />
          </label>
          <Button
            onClick={requestFix}
            disabled={submitting || !runtime?.orchestrator_configured}
            data-testid="fix-with-ai-submit"
          >
            {submitting ? t('fix.requesting') : t('fix.request')}
          </Button>
        </section>
      )}

      <section>
        <h2 style={h2Style}>{t('fix.recent')}</h2>
        {fixRuns.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--reviewer-text-secondary)' }}>{t('fix.none')}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {fixRuns.map((run) => (
              <button
                key={run.fix_run_id}
                onClick={() => navigate(`/fix/${run.fix_run_id}`)}
                style={rowStyle}
              >
                <StatusBadge status={run.status} />
                <span>{run.target_branch}</span>
                <span style={{ color: 'var(--reviewer-text-secondary)' }}>
                  {t('fix.appliedFailed', { applied: run.fixes_applied, failed: run.fixes_failed })}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      {findings.length > 0 && (
        <section>
          <h2 style={h2Style}>{t('fix.openFindings')}</h2>
          {findings.slice(0, 10).map((finding) => (
            <button key={finding.id} onClick={() => navigate(`/finding/${finding.id}`)} style={rowStyle}>
              <SeverityBadge severity={finding.severity} />
              <span>{finding.title}</span>
            </button>
          ))}
        </section>
      )}
    </div>
  )
}

const h1Style = { margin: 0, marginBottom: '8px', fontFamily: 'var(--reviewer-font-mono)', fontSize: 'var(--reviewer-text-2xl)', fontWeight: 500 } as const
const h2Style = { margin: '0 0 12px', fontFamily: 'var(--reviewer-font-mono)', fontSize: '16px' } as const
const subStyle = { margin: 0, fontFamily: 'var(--reviewer-font-mono)', color: 'var(--reviewer-text-secondary)' } as const
const cardStyle = { padding: '20px', border: '1px solid var(--reviewer-border)', backgroundColor: 'var(--reviewer-surface)', display: 'flex', flexDirection: 'column' as const, gap: '12px' }
const labelStyle = { display: 'flex', flexDirection: 'column' as const, gap: '6px' }
const inputStyle = { padding: '8px 12px', fontFamily: 'var(--reviewer-font-mono)', backgroundColor: 'var(--reviewer-bg)', border: '1px solid var(--reviewer-border)', color: 'var(--reviewer-text)' } as const
const rowStyle = { display: 'flex', gap: '12px', alignItems: 'center', width: '100%', textAlign: 'left' as const, padding: '10px 12px', border: '1px solid var(--reviewer-border)', background: 'var(--reviewer-surface)', color: 'var(--reviewer-text)', cursor: 'pointer', fontFamily: 'var(--reviewer-font-mono)' }
function bannerStyle(ok?: boolean) {
  return {
    padding: '12px 16px',
    border: '1px solid var(--reviewer-border)',
    backgroundColor: ok ? 'var(--reviewer-surface)' : 'var(--reviewer-danger-soft, var(--reviewer-surface))',
    fontFamily: 'var(--reviewer-font-mono)',
    fontSize: '13px',
  }
}
